import { NavLink as RouterNavLink, NavLinkProps } from "react-router-dom";
import { forwardRef, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { prefetchRoute } from "@/lib/prefetch";

interface NavLinkCompatProps extends Omit<NavLinkProps, "className"> {
  className?: string;
  activeClassName?: string;
  pendingClassName?: string;
  prefetch?: boolean;
  prefetchDelay?: number;
}

const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName, to, prefetch = true, prefetchDelay = 100, onMouseEnter, onTouchStart, ...props }, ref) => {
    const prefetchTimeoutRef = useRef<NodeJS.Timeout>();

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (prefetch && typeof to === 'string') {
          clearTimeout(prefetchTimeoutRef.current);
          prefetchTimeoutRef.current = setTimeout(() => {
            prefetchRoute(to);
          }, prefetchDelay);
        }
        onMouseEnter?.(e);
      },
      [to, prefetch, prefetchDelay, onMouseEnter]
    );

    const handleTouchStart = useCallback(
      (e: React.TouchEvent<HTMLAnchorElement>) => {
        if (prefetch && typeof to === 'string') {
          prefetchRoute(to);
        }
        onTouchStart?.(e);
      },
      [to, prefetch, onTouchStart]
    );

    return (
      <RouterNavLink
        ref={ref}
        to={to}
        onMouseEnter={handleMouseEnter}
        onTouchStart={handleTouchStart}
        className={({ isActive, isPending }) =>
          cn(className, isActive && activeClassName, isPending && pendingClassName)
        }
        {...props}
      />
    );
  },
);

NavLink.displayName = "NavLink";

export { NavLink };
